import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Toast, useToast } from '@zillow/constellation-10';

import WizardView from './LandlordLeaseView/WizardView';
import ListView from './LandlordLeaseView/ListView';
import SelfServeView from './LandlordLeaseView/SelfServeView';
import { LEASE_VIEW_TYPES } from '../models/Lease';
import { trackError } from '../utils/trackError';
import CosmosView from './LandlordLeaseView/CosmosView';
import LandlordLeasePreview from './LandlordLeasePreview';
import QueryParamActivatedActions from '../components/LeaseActions/QueryParamActivatedActions';
import { PreSubmitCallbackProvider } from '../contexts/PreSubmitCallbackContext';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';
import { useLeaseQuery, useLeasingUserPageQuery } from '../utils/queries';
import Lease from '../models/Lease';
import Property from '../models/Property';

function LandlordLeaseView() {
  const history = useHistory();
  const { enqueueToast } = useToast();
  const { leaseId, viewType, propertyId } = useLeasingUrlParams();

  const leaseQuery = useLeaseQuery(leaseId, { enabled: !!leaseId });
  const lease = leaseQuery.data;

  const leasingUserPageQuery = useLeasingUserPageQuery(leaseId, {
    enabled: !!leaseId && viewType == null,
  });

  useEffect(() => {
    if (leasingUserPageQuery.data && viewType == null) {
      const { page } = leasingUserPageQuery.data;
      if (page === 'SUMMARY') {
        history.replace(Lease.getSummaryPath(leaseId));
      } else {
        history.replace(Lease.getWizardPath(leaseId));
      }
    }
  }, [leasingUserPageQuery.data, leaseId, viewType, history]);

  useEffect(() => {
    if (lease) {
      lease.forceRedirectIfWrongViewType(history, viewType as LEASE_VIEW_TYPES, trackError);
    }
  }, [lease, history, viewType]);

  useEffect(() => {
    if (lease?.isArchived) {
      enqueueToast(<Toast tone="info">This lease has been deleted and can no longer be accessed</Toast>);
      history.push(Property.getLeasesDashboardPath(propertyId));
    }
  }, [lease?.isArchived, history, propertyId, enqueueToast]);

  // Only show loading state, let errors bubble up to error boundary
  if (!lease) {
    return null;
  }

  const leaseViewComponents = {
    [LEASE_VIEW_TYPES.SELF_SERVE]: <SelfServeView lease={lease} />,
    [LEASE_VIEW_TYPES.SUMMARY]: <ListView lease={lease} />,
    [LEASE_VIEW_TYPES.WIZARD]: <WizardView lease={lease} />,
  };

  return (
    <PreSubmitCallbackProvider>
      {lease.journeyId && viewType !== LEASE_VIEW_TYPES.PREVIEW ? (
        <CosmosView lease={lease} />
      ) : (
        leaseViewComponents[viewType]
      )}
      {viewType === LEASE_VIEW_TYPES.PREVIEW && <LandlordLeasePreview />}
      <QueryParamActivatedActions />
    </PreSubmitCallbackProvider>
  );
}

export default LandlordLeaseView;
